import * as React from "react";
import { ApolloError, gql, useMutation } from "@apollo/client";
import { 
  RegisterMutationMutation, 
  RegisterMutationMutationVariables 
} from "../../../../schemaTypes";
import { normalizeErrors } from "../../../../utils/normalizeErrors";
import { NormalizedErrorMap } from "../../../../types/NormalizedErrorMap";

const mapRegisterApolloError = (message: string): NormalizedErrorMap => {
  const normalizedMessage = message.toLowerCase();

  if (normalizedMessage.includes("email")) {
    return { email: message };
  }

  if (normalizedMessage.includes("phone")) {
    return { phone: message };
  }

  if (normalizedMessage.includes("password") && normalizedMessage.includes("match")) {
    return { confirmPassword: message };
  }

  if (normalizedMessage.includes("password")) {
    return { password: message };
  }

  if (normalizedMessage.includes("nda") || normalizedMessage.includes("terms")) {
    return { nda: message };
  }

  return { general: message };
};

const registerMutation = gql`
  mutation RegisterMutation(
    $email: String!
    $phone: String!
    $password: String!
    $confirmPassword: String!
    $nda: Boolean!
  ) {
    register(
      email: $email
      phone: $phone
      password: $password
      confirmPassword: $confirmPassword
      nda: $nda
    ) {
      errors {
        path
        message
      }
      message
    }
  }
`;


interface Props {
  children: (
    data: {
      submit: (
        values: RegisterMutationMutationVariables
      ) => Promise<NormalizedErrorMap | null>;
      loading: boolean;
      error?: Error;
      message?: string;
    }
  ) => React.ReactElement | null;
}


export const RegisterController: React.FC<Props> = ({ children }) => {
  const [_registerMutation, { loading, error, data }] = useMutation<RegisterMutationMutation, RegisterMutationMutationVariables>(registerMutation);

  const submit = async (values: RegisterMutationMutationVariables) => {
    try {
      const response = await _registerMutation({ variables: values });
      const register = response.data?.register;

      if (register?.errors) {
        return normalizeErrors(register.errors);
      }

      return null;
    } catch (mutationError) {
      const apolloError = mutationError as ApolloError;
      const message =
        apolloError.graphQLErrors?.[0]?.message ||
        apolloError.message ||
        "An error occurred";

      return mapRegisterApolloError(message);
    }
  };

  const message = data?.register?.message ?? undefined;

  return children({
    submit,
    loading,
    error,
    message
  });
};